import { User } from "@/models/users/User";
import Workspace, { WorkspaceRole } from "@/models/workspace/Workspace";
import { Item, TextMatch } from "@/pages/workspace/utils/workspaceItemUtils";
import { createContext, Dispatch, SetStateAction, useContext } from "react";
import { WorkspaceSearch } from "./WorkspaceProvider";

/**
 * Outcome of replacing a document's content from its cloud copy. A refusal carries the
 * API's own wording when it has one (e.g. the remote name is already taken by a
 * sibling) so the sync report and the single-file toast can show it.
 */
export type UpdateUploadedFileResult =
  { ok: true } | { ok: false; message: string };

interface WorkspaceContextValue {
  workspace: Workspace | null;
  tenantUsers: User[];
  myUser?: User;
  workspaceOwner?: User;
  isUserOwner: boolean;
  isParentWorkspace: boolean;
  isOrphanedInspection: boolean;
  effectiveRole: WorkspaceRole | null;
  canUserUploadFiles: () => boolean;
  /** Whether the user may move items (drag-and-drop and "Flyt til..."): owner or shared Write. */
  canUserMoveItems: () => boolean;
  canUserDeleteFile: (item: Item) => boolean;
  canUserEditMetadata: (item: Item) => boolean;
  isUserAdmin: () => boolean;
  canChangeOrgSharing: () => boolean;
  canDeleteWorkspace: () => boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  search: WorkspaceSearch;
  setSearch: Dispatch<SetStateAction<WorkspaceSearch>>;
  metadataOptions: string[];
  folderOptions: { name: string; id: string }[];
  selectedMetadataFilters: {
    metadataNames: string[];
  };
  setSelectedMetadataFilters: Dispatch<
    SetStateAction<{
      metadataNames: string[];
    }>
  >;
  filteredWorkspaceItems: Item[];
  /** Documents the search index found by their text for the current search (LAR-235). */
  textMatchIds: ReadonlySet<string>;
  /** Of those, the ones listed only because of their text: their rows say "Fandt N matches". */
  textOnlyMatches: ReadonlyMap<string, TextMatch>;
  handleDeleteDocuments: (
    workspaceId: string,
    documentIds: string[],
    options?: {
      successToastMessage?: string | null;
      errorToastMessage?: string;
    }
  ) => Promise<void>;
  handleUpdateDocumentMetadata: (
    documentId: string,
    metadata: {
      documentSource: string;
      driveId?: string;
      siteId?: string;
      sharepointFileId?: string;
    }
  ) => Promise<void>;
  handleUpdateUploadedFile: (
    file: File,
    documentId: string
  ) => Promise<UpdateUploadedFileResult>;
  canItemBeReIndexed: (item: Item) => boolean;
}

export const WorkspaceContext = createContext<
  WorkspaceContextValue | undefined
>(undefined);

export const useWorkspaceContext = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx)
    throw new Error(
      "useWorkspaceContext must be used within a WorkspaceProvider"
    );
  return ctx;
};
